/**
 * Centralized payroll rate resolution.
 * Single source of truth for matrix types: Trade + Classification + Payroll Type → Hourly Rate.
 * User Rate (Standard): organization_members.hourly_rate, then users.hourly_rate, then actor cache.
 * Non-CCQ (Custom) reuses the Residential matrix rate.
 */

import { devLog } from "./devLog";
import {
  applyOrganizationScope,
  resolveOrganizationIdForQueries,
} from "./orgQueryScope";
import { isEmployee } from "./permissions";
import { fetchOrganizationMemberUserRate } from "./userRate";

/** Matrix lookup key: Non-CCQ sessions keep payroll_type Custom for reporting. */
export function resolveMatrixPayrollType(payrollType) {
  return payrollType === "Custom" ? "Residential" : payrollType;
}

function hasStoredRate(value) {
  return value != null && value !== "";
}

async function resolveUserHourlyRate({
  supabase,
  actor = null,
  supabaseUserId,
  cachedHourlyRate,
  tradeId,
  tradeName,
  classificationName,
  payrollType,
}) {
  const classification = classificationName || "";
  const resolvedTradeName = tradeName || "";
  const organizationId = actor?.organization_id || null;

  const membership = await fetchOrganizationMemberUserRate(
    {
      supabaseUserId,
      organizationId,
    },
    supabase,
  );

  if (membership.error) {
    console.error("MEMBERSHIP USER RATE LOOKUP ERROR:", membership.error);
  }

  if (
    !membership.error &&
    membership.found &&
    hasStoredRate(membership.hourlyRate)
  ) {
    const hourlyRate = Number(membership.hourlyRate) || 0;
    return {
      hourlyRate,
      tradeId: tradeId || null,
      tradeName: resolvedTradeName,
      classificationName: classification,
      payrollType,
      source: "membership",
      missingCustomRate: !hourlyRate,
    };
  }

  const { data: userRow, error } = await supabase
    .from("users")
    .select("hourly_rate")
    .eq("supabase_user_id", supabaseUserId)
    .single();

  if (error) {
    console.error("USER RATE LOOKUP ERROR:", error);
  }

  if (!error && userRow && hasStoredRate(userRow.hourly_rate)) {
    const hourlyRate = Number(userRow.hourly_rate) || 0;
    return {
      hourlyRate,
      tradeId: tradeId || null,
      tradeName: resolvedTradeName,
      classificationName: classification,
      payrollType,
      source: "user",
      missingCustomRate: !hourlyRate,
    };
  }

  if (hasStoredRate(cachedHourlyRate)) {
    const hourlyRate = Number(cachedHourlyRate) || 0;
    return {
      hourlyRate,
      tradeId: tradeId || null,
      tradeName: resolvedTradeName,
      classificationName: classification,
      payrollType,
      source: "user_cached",
      missingCustomRate: !hourlyRate,
    };
  }

  return {
    hourlyRate: 0,
    tradeId: tradeId || null,
    tradeName: resolvedTradeName,
    classificationName: classification,
    payrollType,
    source: error || membership.error ? "user_cached" : "user",
    missingCustomRate: true,
  };
}

async function resolveMatrixHourlyRate({
  supabase,
  actor = null,
  tradeId,
  tradeName,
  classificationName,
  payrollType,
  lookupPayrollType,
}) {
  const classification = classificationName || "";
  const resolvedTradeName = tradeName || "";

  if (!tradeId) {
    devLog("PAYROLL LOOKUP — missing trade_id on profile");
    return {
      hourlyRate: 0,
      tradeId: null,
      tradeName: resolvedTradeName,
      classificationName: classification,
      payrollType,
      source: "table",
      missingCustomRate: false,
    };
  }

  devLog(
    "PAYROLL LOOKUP",
    resolvedTradeName || tradeId,
    classification,
    lookupPayrollType,
    payrollType !== lookupPayrollType ? `(session type ${payrollType})` : "",
  );

  if (isEmployee(actor)) {
    const { data: rpcRate, error: rpcError } = await supabase.rpc(
      "cp_resolve_own_payroll_hourly_rate",
      {
        p_trade_id: tradeId,
        p_classification_name: classification,
        p_payroll_type: lookupPayrollType,
        p_organization_id: actor?.organization_id || null,
      },
    );

    if (!rpcError) {
      const hourlyRate = rpcRate != null ? Number(rpcRate) : 0;
      devLog("RATE RESULT (rpc)", {
        hourlyRate,
        tradeId,
        classification,
        lookupPayrollType,
        payrollType,
      });
      return {
        hourlyRate,
        tradeId,
        tradeName: resolvedTradeName,
        classificationName: classification,
        payrollType,
        source: "rpc",
        missingCustomRate: false,
      };
    }

    console.error("PAYROLL RATE RPC ERROR:", rpcError);
  }

  // Resolve org id first, then sync-scope. Do NOT await applyOrganizationScopeForActor
  // here — awaiting a Postgrest builder executes the query before maybeSingle().
  let rateQuery = supabase
    .from("payroll_rates")
    .select("hourly_rate")
    .eq("trade_id", tradeId)
    .eq("classification_name", classification)
    .eq("payroll_type", lookupPayrollType);

  if (actor) {
    const organizationId = await resolveOrganizationIdForQueries(
      actor,
      supabase,
    );
    rateQuery = applyOrganizationScope(rateQuery, organizationId, {
      tableName: "payroll_rates",
    });
  }

  const { data: rateData, error: rateError } = await rateQuery.maybeSingle();

  if (rateError) {
    console.error("PAYROLL RATE LOOKUP ERROR:", rateError);
  }

  const hourlyRate = rateData?.hourly_rate ? Number(rateData.hourly_rate) : 0;

  devLog("RATE RESULT", {
    hourlyRate,
    tradeId,
    classification,
    lookupPayrollType,
    payrollType,
  });

  return {
    hourlyRate,
    tradeId,
    tradeName: resolvedTradeName,
    classificationName: classification,
    payrollType,
    source: "table",
    missingCustomRate: false,
  };
}

/**
 * Resolve hourly rate for session start / finalize.
 * Employees prefer SECURITY DEFINER RPC (single rate, classification-bound).
 * Manager+ may read payroll_rates directly (org matrix).
 * User Rate (Standard): membership.hourly_rate → users.hourly_rate → actor cache.
 */
export async function resolvePayrollHourlyRate({
  supabase,
  actor = null,
  tradeId,
  tradeName,
  classificationName,
  payrollType,
  supabaseUserId,
  cachedHourlyRate,
}) {
  if (payrollType === "Standard") {
    return resolveUserHourlyRate({
      supabase,
      actor,
      supabaseUserId,
      cachedHourlyRate,
      tradeId,
      tradeName,
      classificationName,
      payrollType,
    });
  }

  const lookupPayrollType = resolveMatrixPayrollType(payrollType);

  return resolveMatrixHourlyRate({
    supabase,
    actor,
    tradeId,
    tradeName,
    classificationName,
    payrollType,
    lookupPayrollType,
  });
}
